import { PIN_LENGTH } from '../../shared/constants.js';
import { isValidPin } from '../../shared/signaling.js';
import { copyInputValue } from './clipboard.js';
import { formatPin, normalizePinInput } from './format.js';
import { canShareNatively, shareLink } from './native-share.js';
import { renderQrCode } from './qr.js';
import { hasCamera } from './qr-scanner.js';
import { t } from './i18n/index.js';
import { openScanOverlay } from './ui/scan-overlay.js';
import { buildPinPath, buildShareLink } from './route.js';
import { runSendSession } from './send-session.js';
import { SignalingClient, signalingUrl } from './signaling-client.js';
import { clear, el, hide, show } from './ui/dom.js';
import { createFileCard } from './ui/file-card.js';
import { createHeader, createErrorCard, createRestartButton, userFacingError } from './ui/shell.js';
import { createStatusPill } from './ui/status.js';

export const createDropZone = (onFile: (file: File) => void): HTMLElement => {
  const input = el('input', { type: 'file', className: 'dropzone__input', id: 'file-input' });
  const label = el('label', { className: 'dropzone' }, [
    el('span', { className: 'dropzone__glyph', text: '⬡' }),
    el('span', { className: 'dropzone__title', text: t('drop.title') }),
    el('span', { className: 'dropzone__hint', text: t('drop.hint') }),
    input,
  ]);
  label.htmlFor = input.id;
  input.addEventListener('change', () => {
    const file = input.files?.[0];
    if (file !== undefined) {
      onFile(file);
    }
  });
  label.addEventListener('dragover', (event) => {
    event.preventDefault();
    label.classList.add('dropzone--over');
  });
  label.addEventListener('dragleave', () => label.classList.remove('dropzone--over'));
  label.addEventListener('drop', (event) => {
    event.preventDefault();
    label.classList.remove('dropzone--over');
    const file = event.dataTransfer?.files[0];
    if (file !== undefined) {
      onFile(file);
    }
  });
  return el('section', { className: 'card' }, [label]);
};

type ShareCard = Readonly<{ element: HTMLElement; render: (link: string, pin: string) => Promise<void> }>;

const createShareCard = (): ShareCard => {
  const linkInput = el('input', { className: 'share__link', type: 'text', readOnly: true, ariaLabel: t('share.linkLabel') });
  const copyButton = el('button', { className: 'button', type: 'button', text: t('share.copy') });
  const shareButton = el('button', { className: 'button button--ghost share__native', type: 'button', text: t('share.native') });
  shareButton.hidden = !canShareNatively(navigator);
  const copyNote = el('div', { className: 'share__note' });
  const canvas = el('canvas', { className: 'share__qr' });
  const pinDigits = el('div', { className: 'pin', ariaLabel: t('share.pinLabel') });
  const element = el('section', { className: 'card share' }, [
    el('div', { className: 'card__title', text: t('share.title') }),
    el('p', { className: 'card__text', text: t('share.text') }),
    el('div', { className: 'share__row' }, [linkInput, copyButton, shareButton]),
    copyNote,
    el('div', { className: 'share__qr-wrap' }, [canvas]),
    el('div', { className: 'share__divider', text: t('share.or') }),
    el('p', { className: 'card__text share__pin-hint', text: t('share.pinHint') }),
    pinDigits,
  ]);
  linkInput.addEventListener('focus', () => linkInput.select());
  copyButton.addEventListener('click', () => {
    copyInputValue(linkInput)
      .then(() => {
        copyNote.textContent = t('share.copied');
        copyNote.className = 'share__note share__note--ok';
      })
      .catch((error: unknown) => {
        copyNote.textContent = userFacingError(error);
        copyNote.className = 'share__note share__note--warn';
        linkInput.focus();
        linkInput.select();
      });
  });
  shareButton.addEventListener('click', () => {
    shareLink(linkInput.value)
      .then((result) => {
        if (result === 'shared') {
          copyNote.textContent = t('share.shared');
          copyNote.className = 'share__note share__note--ok';
        }
      })
      .catch((error: unknown) => {
        copyNote.textContent = userFacingError(error);
        copyNote.className = 'share__note share__note--warn';
      });
  });
  return {
    element,
    render: async (link, pin) => {
      linkInput.value = link;
      pinDigits.textContent = formatPin(pin);
      await renderQrCode(canvas, link);
    },
  };
};

/** Card on the start page for receivers who were given a PIN instead of a link. */
const createPinEntryCard = (): HTMLElement => {
  const input = el('input', { className: 'pin-entry__input', type: 'text', ariaLabel: t('share.pinLabel'), id: 'pin-input' });
  input.inputMode = 'numeric';
  input.autocomplete = 'one-time-code';
  input.placeholder = '000 000 000';
  input.maxLength = PIN_LENGTH + 2;
  const button = el('button', { className: 'button', type: 'submit', text: t('pin.receive') });
  const note = el('div', { className: 'share__note' });
  const form = el('form', { className: 'pin-entry' }, [input, button]);
  const scanButton = el('button', { className: 'button button--ghost button--large pin-entry__scan', type: 'button', text: t('pin.scan') });
  scanButton.hidden = !hasCamera(navigator);
  scanButton.addEventListener('click', () => {
    note.textContent = '';
    openScanOverlay(document.body).catch((error: unknown) => {
      note.textContent = userFacingError(error);
      note.className = 'share__note share__note--warn';
    });
  });
  input.addEventListener('input', () => {
    const digits = normalizePinInput(input.value).slice(0, PIN_LENGTH);
    input.value = formatPin(digits);
  });
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const pin = normalizePinInput(input.value);
    if (!isValidPin(pin)) {
      note.textContent = t('pin.incomplete', { count: PIN_LENGTH });
      note.className = 'share__note share__note--warn';
      input.focus();
      return;
    }
    window.location.assign(buildPinPath(pin));
  });
  return el('section', { className: 'card' }, [
    el('div', { className: 'card__title', text: t('pin.title') }),
    el('p', { className: 'card__text', text: t('pin.text') }),
    form,
    scanButton,
    note,
  ]);
};

const runTransfer = async (
  file: File,
  status: ReturnType<typeof createStatusPill>,
  shareCard: ShareCard,
  progressHost: HTMLElement,
): Promise<void> => {
  const signaling = await SignalingClient.connect(signalingUrl(window.location));
  const peerJoined = signaling.waitFor((message): message is { type: 'peer-joined' } => message.type === 'peer-joined');
  const created = signaling.waitFor(
    (message): message is { type: 'created'; roomId: string; pin: string } => message.type === 'created',
  );
  signaling.send({ type: 'create' });
  const { roomId, pin } = await created;
  await shareCard.render(buildShareLink(window.location.origin, roomId), pin);
  show(shareCard.element);
  status.set('waiting', t('sender.status.waitingReceiver'));

  await runSendSession(file, signaling, peerJoined, {
    onReceiverConnected: () => status.set('active', t('sender.status.receiverConnected')),
    onWaitingForDestination: () => {
      hide(shareCard.element);
      status.set('active', t('sender.status.waitingDestination'));
    },
    onTransferStart: (progress) => {
      status.set('active', t('sender.status.beaming'));
      progressHost.replaceChildren(progress.element);
    },
    onDelivered: () => {
      status.set('success', t('sender.status.delivered'));
      progressHost.append(createRestartButton(t('sender.sendAnother')));
    },
  });
  signaling.close();
};

/** A file handed over by the OS share sheet, or null for a normal visit. */
export type SharedFile = File | null;

export const renderSenderPage = (root: HTMLElement, sharedFile: SharedFile): void => {
  clear(root);
  const main = el('main', { className: 'page' });
  const status = createStatusPill();
  const shareCard = createShareCard();
  const progressHost = el('div');
  const fileHost = el('div');
  const errorHost = el('div');
  hide(status.element);
  hide(shareCard.element);

  const pinEntry = createPinEntryCard();
  const beginTransfer = (file: File): void => {
    clear(errorHost);
    hide(dropZone);
    hide(pinEntry);
    hide(inboxLink);
    fileHost.replaceChildren(createFileCard({ name: file.name, size: file.size, mimeType: file.type }));
    show(status.element);
    status.set('waiting', t('sender.status.creatingRoom'));
    runTransfer(file, status, shareCard, progressHost).catch((error: unknown) => {
      status.set('error', t('status.failed'));
      errorHost.replaceChildren(createErrorCard(userFacingError(error), t('action.startOver'), () => window.location.reload()));
    });
  };
  const dropZone = createDropZone(beginTransfer);

  const inboxLink = el('a', { className: 'button button--ghost button--large', href: '/inbox', text: t('home.inboxLink') });
  main.append(
    createHeader(t('sender.tagline')),
    dropZone,
    pinEntry,
    fileHost,
    status.element,
    shareCard.element,
    progressHost,
    errorHost,
    inboxLink,
  );
  root.append(main);
  if (sharedFile !== null) {
    beginTransfer(sharedFile);
  }
};
